import React, { useEffect, useState } from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Box,
  Typography,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";

import { TREE_ICONS, DESIGN_CHILDREN } from "../../constants/treeConfig";
import { fetchBusinessDomains } from "../../api/memberApi";
import { useAuth } from "../../providers/AuthProvider";

const DesignIcon = TREE_ICONS.design;
const DomainIcon = TREE_ICONS.businessDomain;

const activeStyle = {
  backgroundColor: "rgba(46, 125, 50, 0.08)",
  color: "#2E7D32",
  fontWeight: 700,
  borderLeft: "3px solid #2E7D32",
};

export default function DesignTreeMember() {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [designOpen, setDesignOpen] = useState(true);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (companyId) loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadDomains = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBusinessDomains(companyId);
      setDomains(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const toggleDomain = (domainId) =>
    setExpandedDomains((prev) => ({ ...prev, [domainId]: !prev[domainId] }));

  const toggleGroup = (domainId, groupKey) => {
    const key = `${domainId}_${groupKey}`;
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => location.pathname === path;

  const renderLeafNode = (child, domainId, pl) => {
    const ChildIcon = child.icon;
    const path = `/design/${domainId}/${child.route}`;
    return (
      <ListItemButton
        key={child.key}
        sx={{ pl, ...(isActive(path) ? activeStyle : {}) }}
        onClick={() => navigate(path)}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <ChildIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t(child.labelKey)}
          primaryTypographyProps={{ fontSize: "0.8rem" }}
        />
      </ListItemButton>
    );
  };

  const renderGroupNode = (child, domain) => {
    const domainId = domain.businessDomainId;
    const GroupIcon = child.icon;
    const groupKey = `${domainId}_${child.key}`;
    const isExpanded = expandedGroups[groupKey] || false;

    const visibleChildren = child.children.filter(
      (gc) => !gc.enterpriseOnly || domain.enterprise
    );
    if (visibleChildren.length === 0) return null;

    return (
      <React.Fragment key={child.key}>
        <ListItemButton sx={{ pl: 5 }} onClick={() => toggleGroup(domainId, child.key)}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <GroupIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary={t(child.labelKey)}
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ListItemButton>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {visibleChildren.map((gc) => renderLeafNode(gc, domainId, 7))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const renderDesignChildren = (domain) => {
    const domainId = domain.businessDomainId;
    return DESIGN_CHILDREN.map((child) => {
      if (child.group) return renderGroupNode(child, domain);
      if (child.enterpriseOnly && !domain.enterprise) return null;
      return renderLeafNode(child, domainId, 5);
    });
  };

  return (
    <List dense disablePadding>
      <ListItemButton onClick={() => setDesignOpen(!designOpen)} sx={{ fontWeight: 600 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <DesignIcon sx={{ color: "#2E7D32" }} />
        </ListItemIcon>
        <ListItemText primary={t("tree.design")} primaryTypographyProps={{ fontWeight: 600 }} />
        {designOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={designOpen} timeout="auto" unmountOnExit>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              {error}
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadDomains}>
              再読込
            </Button>
          </Box>
        ) : domains.length === 0 ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              事業ドメインがありません
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadDomains}>
              再読込
            </Button>
          </Box>
        ) : (
          <List dense disablePadding>
            {domains.map((domain) => (
              <React.Fragment key={domain.businessDomainId}>
                <ListItemButton
                  sx={{ pl: 3 }}
                  onClick={() => toggleDomain(domain.businessDomainId)}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <DomainIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={domain.businessDomainName}
                    primaryTypographyProps={{ fontSize: "0.875rem", noWrap: true }}
                  />
                  {expandedDomains[domain.businessDomainId]
                    ? <ExpandLess fontSize="small" />
                    : <ExpandMore fontSize="small" />}
                </ListItemButton>
                <Collapse in={expandedDomains[domain.businessDomainId]} timeout="auto" unmountOnExit>
                  <List dense disablePadding>
                    <ListItemButton
                      sx={{ pl: 5, ...(isActive(`/design/${domain.businessDomainId}`) ? activeStyle : {}) }}
                      onClick={() => navigate(`/design/${domain.businessDomainId}`)}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <DomainIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("businessDomain.title")}
                        primaryTypographyProps={{ fontSize: "0.8rem" }}
                      />
                    </ListItemButton>
                    {renderDesignChildren(domain)}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </Collapse>
    </List>
  );
}
